import React from 'react';
import RouterLink from 'next/link';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import min from 'lodash/min';
import max from 'lodash/max';
import ceil from 'lodash/ceil';
import floor from 'lodash/floor';
import range from 'lodash/range';
import { Box, Link, Text, Flex } from '@qga/roo-ui/components';
import { scrollTop } from 'lib/browser';

const NUMBER_OF_LINKS = 5;

const PageLink = ({ pathname, currentPageNumber, pageNumber, currentQuery, onClick }) => {
  const newQuery = { ...currentQuery, page: pageNumber };
  const linkTo = { pathname, search: queryString.stringify(newQuery) };

  const handleOnClick = () => {
    onClick(pageNumber);
    scrollTop();
  };

  if (pageNumber === currentPageNumber) {
    return (
      <Flex>
        <Box borderRadius="100%" bg="greys.alto">
          <Text px={3} py={1} lineHeight="2" color="greys.charcoal" data-active data-testid="active-page">
            {pageNumber}
          </Text>
        </Box>
      </Flex>
    );
  }

  return (
    <RouterLink href={`${linkTo.pathname}?${linkTo.search}`} passHref shallow>
      <Link underline px={3} py={1} onClick={handleOnClick} color="greys.charcoal" data-testid="page-link">
        {pageNumber}
      </Link>
    </RouterLink>
  );
};

PageLink.propTypes = {
  currentPageNumber: PropTypes.number.isRequired,
  pageNumber: PropTypes.number.isRequired,
  currentQuery: PropTypes.object.isRequired,
  pathname: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

PageLink.defaultProps = {
  onClick: () => {},
};

const PageLinks = ({ page, pathname, currentQuery, pageSize, total, onClick }) => {
  const numberOfLinks = NUMBER_OF_LINKS;
  const numberOfPages = ceil(total / pageSize);
  const maxPossibleStartPage = max([1, numberOfPages - NUMBER_OF_LINKS + 1]);
  const activePageOffset = floor(numberOfLinks / 2);
  const candidateStartPage = max([1, page - activePageOffset]);
  const startPage = min([candidateStartPage, maxPossibleStartPage]);
  const endPage = min([startPage + numberOfLinks, numberOfPages + 1]);
  const pageRange = range(startPage, endPage);

  return (
    <Flex textAlign="center">
      {pageRange.map((pageNumber) => (
        <PageLink
          currentPageNumber={page}
          pageNumber={pageNumber}
          currentQuery={currentQuery}
          pathname={pathname}
          key={pageNumber}
          onClick={onClick}
        />
      ))}
    </Flex>
  );
};

PageLinks.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  currentQuery: PropTypes.shape({}).isRequired,
  pathname: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

PageLinks.defaultProps = {
  onClick: () => {},
};

export default PageLinks;
